import React, { useEffect, useState } from 'react';
import { Paper, Box, Typography, Alert, Stack, Button, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MapComponent from './MapComponent';
import apiClient from '../api';
import samsaraPollingService from '../services/samsaraPollingService';

export default function LiveFleetMap({ onVehicleSelect }) {
  const [points, setPoints] = useState([]);
  const [error, setError] = useState(null);
  const [noConnection, setNoConnection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Filter to only show vehicles with recent trips (last 12 months)
      // This prevents showing old/decommissioned vehicles with stale locations
      const res = await apiClient.get('/samsara/vehicles/locations', {
        params: { limit: 100, onlyWithRecentTrips: true }
      });
      
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      
      // Convert Samsara vehicle location items to customers-like shape expected by MapComponent
      const customers = items.map((v, index) => {
        const customer = {
          name: v.name || v.vehicleName || v.id || 'Vehicle',
          address: {
            coordinates: v.location && v.location.latitude !== undefined && v.location.longitude !== undefined
              ? [v.location.longitude, v.location.latitude] // Note: [lng, lat] format for Google Maps
              : null
          }
        };
        
        return customer;
      }).filter(c => {
        const hasCoordinates = c.address && c.address.coordinates;
        return hasCoordinates;
      });
      
      setPoints(customers);
      setLastRefresh(new Date());
    } catch (err) {
      if (err?.code !== 'ERR_NETWORK' && err?.message !== 'Network Error') {
        console.error('[LiveFleetMap] Error loading fleet data:', err.message);
      }
      
      if (err?.response?.status === 400) {
        // No connection configured: hide component entirely
        setNoConnection(true);
        setError(null);
      } else if (err?.code !== 'ERR_NETWORK' && err?.message !== 'Network Error') {
        setError(err?.response?.data?.message || err.message || 'Failed to load fleet locations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    load();
  };

  useEffect(() => { 
    load();
    
    // Subscribe to polling service for real-time updates
    const unsubscribe = samsaraPollingService.subscribe('vehicleLocations', (data, error) => {
      if (error) {
        return;
      }
      
      if (data?.data && Array.isArray(data.data)) {
        const customers = data.data.map((v) => {
          return {
            name: v.name || v.vehicleName || v.id || 'Vehicle',
            address: {
              coordinates: v.location && v.location.latitude !== undefined && v.location.longitude !== undefined
                ? [v.location.longitude, v.location.latitude]
                : null
            }
          };
        }).filter(c => c.address && c.address.coordinates);
        
        setPoints(customers);
        setLastRefresh(new Date());
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, []);
  
  if (noConnection) {
    return null;
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Live Fleet Map</Typography>
          <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
        </Stack>
        <Alert severity="info">{error}</Alert>
      </Paper>
    );
  }

  const formatLastRefresh = () => {
    if (!lastRefresh) return 'Never';
    const seconds = Math.floor((Date.now() - lastRefresh.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6">Live Fleet Map</Typography>
          {lastRefresh && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {formatLastRefresh()} • {points.length} vehicles
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh vehicle locations">
          <IconButton 
            onClick={handleRefresh} 
            disabled={loading}
            color="primary"
            size="small"
            sx={{ 
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box>
        <MapComponent 
          customers={points} 
          onMarkerClick={onVehicleSelect}
        />
      </Box>
    </Paper>
  );
}


